import { useState, useRef, useEffect } from 'react';
import Options from './Options';

const Internal = ({ onSearch }) => {
  // Form state
  const [search, setSearch] = useState('');
  const [subject, setSubject] = useState('');
  const [location, setLocation] = useState('');
  const [isBulkBilling, setIsBulkBilling] = useState(false);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [isServiceListVisible, setIsServiceListVisible] = useState(false);
  const [showResult, setShowResult] = useState(false);

  // Refs for handling outside clicks and focus management
  const serviceListRef = useRef(null);
  const subjectInputRef = useRef(null);

  // Read URL parameters on mount
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    if (queryParams.get('search')) setSearch(queryParams.get('search'));
    if (queryParams.get('subject')) setSubject(queryParams.get('subject'));
    if (queryParams.get('location')) setLocation(queryParams.get('location'));
  }, []);

  // Toggle filter container visibility
  const toggleFilterContainer = () => {
    setIsFilterVisible(!isFilterVisible);
  };

  // Handle service selection from dropdown
  const handleServiceSelect = (text) => {
    setSearch(text);
    setIsServiceListVisible(false);
    // Focus on subject input after selecting a service
    if (subjectInputRef.current) {
      subjectInputRef.current.focus();
    }
  };

  // Form submission handler
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!search.trim()) return;

    setIsLoading(true);
    setShowResult(true);

    // Determine search type based on search term
    let type = 'funding_agencies';
    const searchLower = search.toLowerCase();
    if (searchLower.includes('investor') || searchLower === 'investors') {
      type = 'investors';
    } else if (searchLower.includes('standard') || searchLower === 'standards') {
      type = 'standards';
    }

    // Prepare search params - set page to 1 for new searches
    const params = {
      search,
      subject,
      location,
      type,
      type_of: isBulkBilling ? 'bulk_billing' : '',
      page: 1 // Reset to page 1 for new searches
    };

    // Send search parameters to parent component
    if (onSearch) {
      onSearch(params);

      // Add a short delay to show the loading state more visibly
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  };

  // Handle clicks outside of dropdowns to close them
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (serviceListRef.current && !serviceListRef.current.contains(event.target) &&
        event.target !== document.querySelector('input[placeholder="Search"]')) {
        setIsServiceListVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>

      <Options handleServiceSelect={handleServiceSelect} />
      <h2 className="font-bold text-xl text-white text-center mb-5">
        Look for the information in our internal database
      </h2>
      <form
        className="relative md:shadow rounded-full block md:flex justify-center max-w-[1000px] mx-auto"
        onSubmit={handleSubmit}
      >
        {/* Search input */}
        <div className="w-[100%] py-3 pl-5 relative flex gap-2 items-center md:w-[30%] mb-2 md:mb-0 rounded-full md:rounded-e-none border border-[#2d6bd8] md:border-r-0  text-gray-500 px-2">
          <i className="fa-solid opacity-75 fa-magnifying-glass"></i>
          <input
            required
            className="font-semibold outline-none w-full text-white placeholder:text-white"
            placeholder="Search"
            type="text"
            autoComplete="off"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setIsServiceListVisible(true)}
            disabled={isLoading}
          />

          {isServiceListVisible && (
            <div
              ref={serviceListRef}
              className="absolute top-[101%] w-[90%] overflow-y-scroll max-h-[500px] bg-white text-sm p-2 z-10 shadow-lg"
            >
              <div className="font-bold">Popular Searches</div>
              <div className="pl-2">
                <div
                  className="service-item mt-2 cursor-pointer hover:text-[#2d6bd8]"
                  onClick={() => handleServiceSelect('Investors')}
                >
                  Investors
                </div>
                <div
                  className="service-item mt-2 cursor-pointer hover:text-[#2d6bd8]"
                  onClick={() => handleServiceSelect('Funding Agencies')}
                >
                  Funding Agencies
                </div>
                <div
                  className="service-item mt-2 cursor-pointer hover:text-[#2d6bd8]"
                  onClick={() => handleServiceSelect('Standards')}
                >
                  Standards
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Subject input */}
        <div className="w-[100%] md:w-[30%] relative py-3 pl-5 md:pl-0 flex gap-2 items-center mb-2 md:mb-0 rounded-full md:rounded-none border md:border-x-0 border-[#2d6bd8] text-gray-500 px-2">
          <i className="fa-solid opacity-75 fa-location"></i>
          <input
            ref={subjectInputRef}
            className="font-semibold outline-none w-full text-white placeholder:text-white"
            placeholder="Subject"
            type="text"
            autoComplete="off"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={isLoading}
          />
        </div>

        {/* Location input */}
        <div className="w-[100%] md:w-[30%] relative py-3 pl-5 md:pl-0 flex gap-2 items-center mb-2 md:mb-0 rounded-full md:rounded-none border md:border-x-0 border-[#2d6bd8] text-gray-500 px-2">
          <i className="fa-solid opacity-75 fa-location"></i>
          <input
            className="font-semibold outline-none w-full text-white placeholder:text-white"
            placeholder="Location"
            type="text"
            autoComplete="off"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            disabled={isLoading}
          />
        </div>

        {/* Search button */}
        <div className="flex items-center">
          <div className="w-full md:w-[130px] md:space-x-4 text-white rounded-s-full md:rounded-s-none md:border-l-0 rounded-e-full md:p-1 cursor-pointer flex justify-center items-center border border-[#2d6bd8]">
            {!isLoading ? (
              <button
                type="submit"
                className="main_btn border-2 border-[#fbbb04] duration-300 transition-all"
              >
                <i className="fa-solid text-xl font-bold  fa-magnifying-glass"></i>
                <span className="text-sm font-semibold">Search</span>
              </button>
            ) : (
              <button
                className="loading-button flex gap-2 w-full items-center justify-center p-3 px-4 rounded-full bg-[#2d6bd8] text-white border-2 border-[#2d6bd8]"
                disabled
              >
                <i className="fa-solid text-lg font-bold animate-spin fa-spinner"></i>
                <span className="text-sm font-semibold">Searching...</span>
              </button>
            )}
          </div>
        </div>

        {/* Filter container */}
        {isFilterVisible && (
          <div className="filter-container absolute top-[101%] w-[100%] min-h-[100px] bg-white text-sm py-2 z-10 shadow-lg">
            <div className="p-5">
              <div className="flex justify-between">
                <div className="font-bold text-xl mb-5">Filter</div>
                <div
                  className="text-red-500 cursor-pointer"
                  onClick={toggleFilterContainer}
                >
                  <i className="fa-solid text-xl fa-times"></i>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  className="w-5 h-5"
                  style={{ display: 'block' }}
                  id="bulk_billing"
                  checked={isBulkBilling}
                  onChange={(e) => setIsBulkBilling(e.target.checked)}
                />
                <label htmlFor="bulk_billing">Bulk Billing</label>
              </div>
            </div>
          </div>
        )}
      </form>

      {/* Loading indicator */}
      {showResult && isLoading && (
        <div className="search-result-container max-w-[1200px] mt-6 mx-auto">
          <div className="loading-animation flex flex-col items-center justify-center p-10 bg-gray-100 rounded-2xl">
            <div className="animate-bounce bg-[#2d6bd8] p-2 w-10 h-10 ring-1 ring-[#2d6bd8] shadow-lg rounded-full flex items-center justify-center mb-3">
              <i className="fa-solid fa-magnifying-glass text-white"></i>
            </div>
            <p className="text-gray-600 font-medium">Searching our internal database...</p>
            <div className="mt-3 h-2 w-48 bg-gray-200 rounded-full overflow-hidden">
              <div className="animate-pulse h-full bg-[#2d6bd8] rounded-full"></div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Internal;